import React, { useState, useEffect } from 'react';
import { reddit as redditApi } from '../../api/client';
import { MessageSquare, Search, ExternalLink, Loader2, ArrowUp, Eye, Send } from 'lucide-react';

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-rose-50 text-rose-700",
    high: "bg-rose-100 text-rose-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-stone-100 text-stone-600",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]}`}>{children}</span>;
};

const StatCard = ({ label, value }) => (
  <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
    <span className="text-xs font-medium tracking-widest uppercase text-stone-500">{label}</span>
    <div className="font-serif text-2xl text-stone-900 dark:text-white mt-1">{value}</div>
  </div>
);

export default function RedditPage({ activeBrandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [replyThread, setReplyThread] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [discovering, setDiscovering] = useState(false);

  const loadData = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const result = await redditApi.list(activeBrandId, params);
      setData(result);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [activeBrandId, filter]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      await redditApi.discover(activeBrandId);
      loadData();
    } catch (e) { console.error(e); }
    finally { setDiscovering(false); }
  };

  const handlePost = async () => {
    if (!replyContent.trim() || !replyThread) return;
    setPosting(true);
    try {
      await redditApi.update(activeBrandId, { threadId: replyThread.thread_id, content: replyContent });
      setReplyThread(null);
      setReplyContent('');
      loadData();
    } catch (e) { console.error(e); }
    finally { setPosting(false); }
  };

  const handleSkip = async (threadId) => {
    try {
      await redditApi.update(activeBrandId, { threadId, status: 'SKIPPED' });
      loadData();
    } catch (e) { console.error(e); }
  };

  const relevanceVariant = (score) => score >= 80 ? 'high' : score >= 50 ? 'medium' : 'low';
  const relevanceLabel = (score) => score >= 80 ? 'HIGH' : score >= 50 ? 'MED' : 'LOW';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Discovery</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Reddit Engine</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleDiscover} disabled={discovering}
            className="px-4 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 flex items-center gap-1">
            {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Discover
          </button>
        </div>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Threads" value={data.stats.totalThreads} />
          <StatCard label="Posts Live" value={data.stats.postsLive} />
          <StatCard label="Total Karma" value={`+${data.stats.totalKarma}`} />
          <StatCard label="Est. Citations" value={`~${data.stats.estimatedCitations}`} />
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'NEW', 'QUEUED', 'POSTED', 'SKIPPED'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm ${filter === f ? 'bg-stone-900 text-white dark:bg-white dark:text-stone-900' : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600'}`}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {/* Reply Modal */}
      {replyThread && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setReplyThread(null)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-2">Write Reply</h2>
            <p className="text-sm text-stone-500 mb-4">r/{replyThread.subreddit} &bull; {replyThread.title}</p>
            <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)}
              className="w-full h-40 px-4 py-3 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900"
              placeholder="Write a helpful, authentic reply..." />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReplyThread(null)} className="px-4 py-2 text-sm border border-stone-200 text-stone-600">Cancel</button>
              <button onClick={handlePost} disabled={posting} className="px-4 py-2 text-sm bg-stone-900 text-white flex items-center gap-1">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="space-y-3">
          {(data?.threads || []).length === 0 ? (
            <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No threads found. Click Discover to find relevant threads.</div>
          ) : (
            (data?.threads || []).map((thread) => (
              <div key={thread.thread_id} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 hover:border-stone-400 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-stone-500">r/{thread.subreddit}</span>
                      <span className="text-stone-300">&bull;</span>
                      <span className="text-sm text-stone-400 flex items-center gap-1"><ArrowUp className="w-3 h-3" />{thread.upvotes}</span>
                      <span className="text-stone-300">&bull;</span>
                      <span className="text-sm text-stone-400">{thread.comments} comments</span>
                      <Badge variant={relevanceVariant(thread.relevance)}>{relevanceLabel(thread.relevance)}</Badge>
                    </div>
                    <p className="font-medium text-stone-900 dark:text-white">{thread.title}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={thread.status === 'POSTED' ? 'success' : thread.status === 'SKIPPED' ? 'default' : 'warning'}>{thread.status}</Badge>
                      {thread.karma !== null && thread.karma !== undefined && <span className="text-xs text-stone-400">Karma: +{thread.karma}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={thread.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-stone-100 dark:hover:bg-stone-700 rounded">
                      <ExternalLink className="w-4 h-4 text-stone-400" />
                    </a>
                    {thread.status === 'NEW' && (
                      <>
                        <button onClick={() => handleSkip(thread.thread_id)} className="px-3 py-1 text-xs border border-stone-200 text-stone-500">Skip</button>
                        <button onClick={() => setReplyThread(thread)} className="px-3 py-1 text-xs bg-stone-900 text-white dark:bg-white dark:text-stone-900">Write Reply</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
